import { View } from "@pimo/pimo-app-builder";
import {
  DragAndDropLayout,
  PMBarChartCard,
  PMDonutChartCard,
} from "@pimo/pimo-components";
import { ReportingItem } from "vmd-types";

import { TestAppState } from "../../../app";
import { DONUT_CARD_LAYOUT, DONUT_CHART_COLORS } from "../../../constants";
import { withTotalInTitle } from "../with-total-in-title";

const getProcessStepsData = (items: ReportingItem[] | undefined) => {
  const stepCounts = new Map<string, number>();

  for (const item of items ?? []) {
    const steps = item.basicItem?.processSteps ?? [];
    for (const step of steps) {
      if (!step) continue;
      stepCounts.set(step, (stepCounts.get(step) ?? 0) + 1);
    }
  }

  const entries = Array.from(stepCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label, count], index) => ({
      label,
      count,
      color: DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length],
    }));

  return { entries };
};

/** Adds the process-steps bar chart and donut chart (#2222). */
export function addProcessStepsCards(
  view: View<TestAppState, DragAndDropLayout>
) {
  // Process Steps
  const processStepsBarChartComponent = view.addComponent({
    component: PMBarChartCard,
    layoutProps: {
      componentId: "processStepsBarChartComponent",
      labels: ["barChart", "chart", "processStep"],
      description: "Shows systems grouped by process step.",
      title: "Systems per Process Step",
    },
  });

  processStepsBarChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getProcessStepsData(items);

    return {
      title: "Systems per Process Step",
      categories: entries.map((entry) => entry.label),
      data: entries.map((entry) => entry.count),
      chartType: 1 as const,
      xLabel: "Systems",
      yLabel: "Process Step",
      colors: entries.map((entry) => entry.color),
      fixedHeight: "320px",
    };
  });

  const processStepsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "processStepsDonutChartComponent",
      labels: ["donutChart", "chart", "processStep"],
      description: "Shows systems grouped by process step.",
      title: "Systems per Process Step",
    },
  });

  processStepsDonutChartComponent.mapState(({ currentReport }) => {
    const items = (currentReport?.project?.reportingItems ?? []).filter(
      (item) => !item?.isChannelHeader
    );
    const { entries } = getProcessStepsData(items);

    return {
      title: withTotalInTitle("Systems per Process Step", items.length),
      icon: "DonutLarge",
      ...DONUT_CARD_LAYOUT,
      data: entries.map((entry) => ({
        label: entry.label,
        value: entry.count,
        color: entry.color,
      })),
    };
  });
}
